import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  doc, getDoc, getDocFromServer, onSnapshot, setDoc, serverTimestamp,
} from "firebase/firestore";
import { firebaseAuth, firestore, isFirebaseConfigured } from "@/lib/firebase";
import { isAdminRole, normalizeRole } from "@/lib/rbac";

const AuthContext = createContext(null);
const BUILTIN_ADMIN_EMAILS = new Set(
  String(process.env.REACT_APP_ADMIN_EMAILS || "joshua@gosecond.org")
    .split(",")
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean)
);

/** Firestore field is `role` (lowercase). Console sometimes adds `Role`. */
function roleFromProfile(profile) {
  if (!profile || typeof profile !== "object") return undefined;
  const r = profile.role ?? profile.Role;
  return r;
}

function isFirestorePermissionDenied(err) {
  const code = String(err?.code || "");
  const msg = String(err?.message || "");
  return (
    code === "permission-denied" ||
    code.endsWith("/permission-denied") ||
    /permission/i.test(msg)
  );
}

/**
 * If Auth exists but Firestore `users/{uid}` is missing (e.g. user added only in Firebase Console → Authentication),
 * create a minimal student profile. Rules: create allowed when auth.uid == userId and role == "student".
 */
async function provisionStudentProfileIfMissing(fbUser) {
  if (!firestore || !fbUser?.uid) return;
  const email = (fbUser.email || "").trim().toLowerCase();
  const ref = doc(firestore, "users", fbUser.uid);
  try {
    const snap = await getDocFromServer(ref).catch(() => null);
    if (snap?.exists()) return;
    await setDoc(ref, {
      name: fbUser.displayName || (email ? email.split("@")[0] : "User"),
      email: email || "",
      role: "student",
      bio: "",
      avatar: fbUser.photoURL || "",
      is_active: true,
      communityIds: [],
      created_at: serverTimestamp(),
    });
  } catch (_) {
    /* permission denied or concurrent create */
  }
}

async function loadFirestoreUserProfile(fbUser, auth) {
  if (!firestore || !fbUser?.uid) return {};
  const uid = fbUser.uid;
  if (auth) await auth.authStateReady();
  await fbUser.getIdToken(true);
  const ref = doc(firestore, "users", uid);
  const email = (fbUser.email || "").trim().toLowerCase();
  const deletedByUidRef = doc(firestore, "deleted_accounts", uid);
  const deletedByEmailRef = email ? doc(firestore, "deleted_accounts", `email:${email}`) : null;
  try {
    const [deletedUidSnap, deletedEmailSnap] = await Promise.all([
      getDocFromServer(deletedByUidRef).catch(() => null),
      deletedByEmailRef ? getDocFromServer(deletedByEmailRef).catch(() => null) : Promise.resolve(null),
    ]);
    if (deletedUidSnap?.exists() || deletedEmailSnap?.exists()) {
      return { __deleted_account: true };
    }
    const snap = await getDocFromServer(ref);
    if (!snap.exists()) {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          `[Auth] No Firestore document at users/${uid}. In Firebase Console, create a document whose ID equals this UID (Authentication → User UID) and set role to "admin".`
        );
      }
      return { __missing_profile: true };
    }
    return snap.data();
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[Auth] Server profile read failed:", err?.code || err?.message || err);
    }
    if (isFirestorePermissionDenied(err)) {
      if (process.env.NODE_ENV === "development") {
        console.error(
          "[Auth] Firestore blocked read of users/%s (permission). Usually: rules not published for this project, wrong REACT_APP_FIREBASE_PROJECT_ID, or App Check enforcing without a debug token. Fix: Firebase Console → Firestore → Rules → paste firebase/firestore.rules from this repo → Publish. Or CLI: npx firebase-tools deploy --only firestore:rules --project YOUR_PROJECT_ID. App Check: Build → App Check → disable enforcement for Firestore while testing.",
          uid
        );
      }
      return { __missing_profile: true };
    }
    try {
      const snap = await getDoc(ref);
      if (snap.exists() && process.env.NODE_ENV === "development") {
        console.warn(
          "[Auth] Using possibly stale cached profile (server read failed). Sign out and back in, or use a normal browser tab if you are in an embedded preview."
        );
      }
      return snap.exists() ? snap.data() : { __missing_profile: true };
    } catch {
      return { __missing_profile: true };
    }
  }
}

function titleCaseSegment(s) {
  if (!s) return "";
  const x = String(s).trim().toLowerCase();
  return x ? x.charAt(0).toUpperCase() + x.slice(1) : "";
}

function deriveFirstNameFromName(resolvedName, email) {
  const n = String(resolvedName || "").trim();
  if (n.includes(" ")) return titleCaseSegment(n.split(/\s+/)[0]);
  if (n.includes(".")) return titleCaseSegment(n.split(".")[0]);
  if (n) return titleCaseSegment(n);
  const local = String(email || "").split("@")[0] || "";
  if (local.includes(".")) return titleCaseSegment(local.split(".")[0]);
  return titleCaseSegment(local);
}

function mergeUser(fbUser, profile = {}) {
  const name =
    profile.name ||
    fbUser.displayName ||
    (fbUser.email ? fbUser.email.split("@")[0] : "User");
  const email = (fbUser.email || "").trim().toLowerCase();
  const normalizedRole = normalizeRole(roleFromProfile(profile));
  const resolvedRole = BUILTIN_ADMIN_EMAILS.has(email) ? "admin" : normalizedRole;
  const first_name =
    String(profile.first_name || profile.firstName || "").trim() ||
    deriveFirstNameFromName(name, fbUser.email);
  const learnerOverride = String(
    profile.learner_level_override || profile.learnerLevelOverride || ""
  ).trim();
  const positionFunction = String(
    profile.position_function ?? profile.positionFunction ?? ""
  ).trim();
  const missionStatement = String(
    profile.mission_statement ?? profile.missionStatement ?? ""
  ).trim();
  const companyOrganization = String(
    profile.company_organization ?? profile.companyOrganization ?? ""
  ).trim();
  return {
    id: fbUser.uid,
    _id: fbUser.uid,
    email: fbUser.email,
    name,
    first_name,
    firstName: first_name,
    /** Role stored in Firestore (not elevated by REACT_APP_ADMIN_EMAILS). Used for learner-only rules e.g. mastery gating. */
    firestore_role: normalizedRole,
    firestoreRole: normalizedRole,
    role: resolvedRole,
    bio: profile.bio ?? "",
    avatar: profile.avatar ?? fbUser.photoURL ?? "",
    position_function: positionFunction,
    positionFunction,
    mission_statement: missionStatement,
    missionStatement,
    company_organization: companyOrganization,
    companyOrganization,
    is_active: isUserActive(profile),
    learner_level_override: learnerOverride,
    learnerLevelOverride: learnerOverride,
  };
}

function isUserActive(profile = {}) {
  const raw = profile?.is_active;
  if (raw === false) return false;
  if (raw === 0) return false;
  if (typeof raw === "string" && raw.trim().toLowerCase() === "false") return false;
  return true;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionIdRef = useRef(getOrCreateSessionId());
  const sessionUnsubRef = useRef(null);
  const heartbeatRef = useRef(null);

  useEffect(() => {
    if (!isFirebaseConfigured || !firebaseAuth || !firestore) {
      setUser(false);
      setLoading(false);
      return undefined;
    }

    return onAuthStateChanged(firebaseAuth, async (fbUser) => {
      if (!fbUser) {
        localStorage.removeItem("access_token");
        setUser(false);
        setLoading(false);
        return;
      }

      try {
        let profile = await loadFirestoreUserProfile(fbUser, firebaseAuth);
        if (profile?.__missing_profile) {
          await provisionStudentProfileIfMissing(fbUser);
          profile = await loadFirestoreUserProfile(fbUser, firebaseAuth);
        }
        if (profile?.__missing_profile) {
          await signOut(firebaseAuth);
          localStorage.removeItem("access_token");
          setUser(false);
          setLoading(false);
          return;
        }
        const merged = mergeUser(fbUser, profile);
        if (merged.is_active === false) {
          await signOut(firebaseAuth);
          localStorage.removeItem("access_token");
          setUser(false);
          setLoading(false);
          return;
        }
        await establishSingleSession(merged.id, sessionIdRef.current);
        if (sessionUnsubRef.current) {
          try {
            sessionUnsubRef.current();
          } catch (_) {
            /* Firestore client can throw after internal assertion failure; still clear ref */
          }
          sessionUnsubRef.current = null;
        }
        const sessionRef = doc(firestore, "sessions", merged.id);
        sessionUnsubRef.current = onSnapshot(
          sessionRef,
          async (snap) => {
            const activeId = snap.data()?.activeSessionId;
            if (activeId && activeId !== sessionIdRef.current) {
              localStorage.removeItem("access_token");
              setUser(false);
              if (firebaseAuth) await signOut(firebaseAuth);
            }
          },
          (err) => {
            if (process.env.NODE_ENV === "development") {
              console.warn("[Auth] sessions snapshot (check firestore.rules):", err?.code || err?.message);
            }
          },
        );
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        heartbeatRef.current = setInterval(() => {
          setDoc(sessionRef, { activeSessionId: sessionIdRef.current, updated_at: serverTimestamp() }, { merge: true }).catch(() => {});
        }, 60000);
        setUser(merged);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[Auth] onAuthStateChanged profile load:", err?.message || err);
        }
        setUser(mergeUser(fbUser, {}));
      } finally {
        setLoading(false);
      }
    });
  }, []);

  const login = async (email, password) => {
    if (!firebaseAuth) throw new Error("Firebase Auth is not configured.");
    const cred = await signInWithEmailAndPassword(firebaseAuth, email, password);
    let profile = await loadFirestoreUserProfile(cred.user, firebaseAuth);
    if (profile?.__deleted_account) {
      await signOut(firebaseAuth);
      localStorage.removeItem("access_token");
      setUser(false);
      throw new Error("This account was deleted. Please contact admin.");
    }
    if (profile?.__missing_profile) {
      await provisionStudentProfileIfMissing(cred.user);
      profile = await loadFirestoreUserProfile(cred.user, firebaseAuth);
    }
    if (profile?.__missing_profile) {
      await signOut(firebaseAuth);
      localStorage.removeItem("access_token");
      setUser(false);
      throw new Error(
        "Account exists but your profile could not be created. Check Firestore rules are deployed, or ask an admin to add users/{yourUid} in Firestore.",
      );
    }
    const u = mergeUser(cred.user, profile);
    if (!isUserActive(profile) || u.is_active === false) {
      await signOut(firebaseAuth);
      localStorage.removeItem("access_token");
      setUser(false);
      throw new Error("Your account is suspended. Please contact admin.");
    }
    await establishSingleSession(u.id, sessionIdRef.current);
    setUser(u);
    return u;
  };

  const register = async (name, email, password) => {
    throw new Error("Self-registration is disabled. Please contact admin.");
  };

  const logout = async () => {
    clearSessionWatch();
    if (firebaseAuth) await signOut(firebaseAuth);
    localStorage.removeItem("access_token");
    setUser(false);
  };

  const refreshUser = async () => {
    const fbUser = firebaseAuth?.currentUser;
    if (!fbUser || !firestore) return;
    const profile = await loadFirestoreUserProfile(fbUser, firebaseAuth);
    if (!isUserActive(profile)) {
      await signOut(firebaseAuth);
      localStorage.removeItem("access_token");
      setUser(false);
      return;
    }
    setUser(mergeUser(fbUser, profile));
  };

  const clearSessionWatch = () => {
    if (sessionUnsubRef.current) {
      try {
        sessionUnsubRef.current();
      } catch (_) {
        /* Avoid surfacing teardown errors if the watch client is already broken */
      }
      sessionUnsubRef.current = null;
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  useEffect(() => () => clearSessionWatch(), []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        refreshUser,
        isAdmin: Boolean(user && typeof user === "object" && isAdminRole(user.role)),
        isStudent: Boolean(user && typeof user === "object" && normalizeRole(user.role) === "student"),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;

async function establishSingleSession(uid, sessionId) {
  if (!firestore || !uid || !sessionId) return;
  try {
    const ref = doc(firestore, "sessions", uid);
    await setDoc(ref, { activeSessionId: sessionId, updated_at: serverTimestamp() }, { merge: true });
  } catch (err) {
    // Without Firestore rules for `sessions/{uid}`, login would fail for everyone. Rules should allow
    // owner read/write; if deploy lags, still let the user sign in (single-session enforcement may not run).
    if (process.env.NODE_ENV === "development") {
      console.warn("[Auth] sessions write failed (check firestore.rules for match /sessions/{userId}):", err?.code || err?.message || err);
    }
  }
}

function getOrCreateSessionId() {
  const key = "gosecond_session_id";
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const id = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  sessionStorage.setItem(key, id);
  return id;
}
